/**
 * ImpersonationBanner — persistent red bar shown at the top of every page
 * while the current session is impersonated. One-click "Return to my
 * account" restores the original super admin session.
 *
 * Reads state from localStorage.impersonated_by (set by usersApi.impersonate
 * response). Backend enforces that impersonate is Super Admin only.
 */
import React, { useState } from 'react';
import { AlertTriangle, LogOut, Loader2 } from 'lucide-react';
import { usersApi } from '../lib/api';
import { toast } from 'sonner';

export function ImpersonationBanner() {
  const [busy, setBusy] = useState(false);
  const raw = (() => {
    try { return JSON.parse(localStorage.getItem('impersonated_by') || 'null'); }
    catch { return null; }
  })();
  const impersonatedUserRaw = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
  })();

  if (!raw?.id) return null;

  const handleReturn = async () => {
    setBusy(true);
    try {
      const r = await usersApi.stopImpersonation();
      const { user, locations: origLocations } = r.data || {};
      // The backend rotates the httpOnly cookie back to the super admin's
      // token in the response — we don't touch auth_token here.
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('permissions', JSON.stringify(user.permissions || {}));
      }
      if (Array.isArray(origLocations)) {
        localStorage.setItem('locations', JSON.stringify(origLocations));
        if (origLocations.length > 0) {
          localStorage.setItem('current_location', JSON.stringify(origLocations[0]));
        }
      }
      localStorage.removeItem('impersonated_by');
      localStorage.removeItem('nav_order');
      toast.success(`Back to ${user?.name || 'your account'}`);
      // Full reload so every module re-fetches with the restored session.
      window.location.href = '/';
    } catch (e) {
      setBusy(false);
      toast.error('Return failed — ' + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div
      className="w-full bg-rose-600 text-white text-sm font-medium flex items-center justify-center gap-3 py-2 px-4 shadow-md"
      data-testid="impersonation-banner"
      role="alert"
    >
      <AlertTriangle size={14} className="shrink-0" />
      <span>
        Viewing as{' '}
        <span className="font-bold">
          {impersonatedUserRaw?.name || impersonatedUserRaw?.email || 'user'}
        </span>
        {' '}· started by <span className="font-semibold">{raw.name || raw.email}</span>
      </span>
      <button
        type="button"
        onClick={handleReturn}
        disabled={busy}
        className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-white/20 hover:bg-white/30 px-3 py-0.5 text-xs font-semibold transition-colors disabled:opacity-60"
        data-testid="impersonation-return-btn"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />}
        Return to my account
      </button>
    </div>
  );
}

export default ImpersonationBanner;
